import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';

// Icons
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import conversationUIStyles from '../styles/conversationUIStyles';

const AISuggestionOverlay = ({
  suggestion,
  isEditing,
  editedText,
  onEditChange,
  onSendAsIs,
  onStartEdit,
  onCancelEdit,
  onSendEdited,
  onDiscard,
  isSending,
  show,
  styles = conversationUIStyles,
  responsive = {}
}) => {
  const [localEditText, setLocalEditText] = useState(editedText || '');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (show) {
      setAnimateIn(true);
    }
  }, [show]);

  useEffect(() => {
    setLocalEditText(editedText || suggestion || '');
  }, [editedText, suggestion]);

  const handleLocalEditChange = (e) => {
    setLocalEditText(e.target.value);
    if (onEditChange) {
      onEditChange(e);
    }
  };

  const handleSendEdited = () => {
    if (localEditText.trim()) {
      onSendEdited();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      if (isEditing) {
        handleSendEdited();
      } else {
        onSendAsIs();
      }
    }
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      if (isEditing) {
        onCancelEdit();
      } else {
        onDiscard();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isEditing]);

  if (!show || !suggestion) {
    return null;
  }

  return (
    <Fade in={animateIn} timeout={300}>
      <Box sx={styles.suggestionOverlay}>
        {/* Header */}
        <Box sx={styles.suggestionHeader}>
          <Box sx={styles.suggestionTitle}>
            <AutoAwesomeIcon sx={{ color: '#FF9800' }} />
            <Typography variant="subtitle2" fontWeight={600}>
              AI Suggestion
            </Typography>
            <Chip
              label="Assisted Mode"
              size="small"
              sx={{
                backgroundColor: '#FFF3E0',
                color: '#F57C00',
                fontWeight: 500,
                height: 20,
                fontSize: '11px'
              }}
            />
          </Box>
          
          <IconButton
            size="small"
            onClick={onDiscard}
            aria-label="Discard suggestion"
            sx={{
              color: '#94a3b8',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
                color: '#ef5350'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={localEditText}
            onChange={handleLocalEditChange}
            onKeyDown={handleKeyPress}
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFF3E0',
                '&.Mui-focused': {
                  backgroundColor: '#ffffff'
                }
              }
            }}
            placeholder="Edit the suggestion..."
          />
        ) : (
          <Box sx={styles.suggestionContent}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {suggestion}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={styles.suggestionActions}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelEdit}
                disabled={isSending}
                sx={{ textTransform: 'none' }}
              >
                Cancel Edit
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSendEdited}
                disabled={isSending || !localEditText.trim()}
                endIcon={isSending ? <CircularProgress size={16} /> : <SendIcon />}
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)'
                  }
                }}
              >
                Send Edited
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="text"
                size="small"
                onClick={onDiscard}
                disabled={isSending}
                sx={{ 
                  textTransform: 'none',
                  color: '#94a3b8',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    color: '#ef5350'
                  }
                }}
              >
                Discard
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={onStartEdit}
                disabled={isSending}
                startIcon={<EditIcon />}
                sx={{
                  textTransform: 'none',
                  borderColor: '#FF9800',
                  color: '#FF9800',
                  '&:hover': {
                    borderColor: '#F57C00',
                    backgroundColor: 'rgba(255,152,0,0.04)'
                  }
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={onSendAsIs}
                disabled={isSending}
                endIcon={isSending ? <CircularProgress size={16} /> : <CheckIcon />}
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)'
                  }
                }}
              >
                Send as Is
              </Button>
            </>
          )}
        </Box>

        {/* Helper text */}
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Press Enter to send • Escape to dismiss
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

export default AISuggestionOverlay;